import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core'
import statsTableStyles from './statsTable.styles'
import StatsTableHeader from './StatsTableHeader'
import {
  STATS_GROUPS,
  flattenLeaves,
  totalCells,
  averageCells,
} from '../../utils/constants/playerStatsColumnGroups'
import {
  getVisibleFederationLeaves,
  filterFederationGroups,
} from '../../utils/helpers/displayStatsMapping'

const useStyles = makeStyles(statsTableStyles)

const PlayerCompetitionStatsTable = ({ competition, onSeasonClick, displayStats }) => {
  const classes = useStyles()
  const seasons = competition?.seasons || []
  if (seasons.length === 0) return null

  const totalsRow = totalCells(competition.totals)
  const averagesRow = averageCells(competition.averages)

  const visibleLeafKeys = getVisibleFederationLeaves(displayStats)
  const groups = filterFederationGroups(STATS_GROUPS, visibleLeafKeys)
  const LEAVES = flattenLeaves(groups)

  return (
    <TableContainer component={Paper} className={classes.container}>
      <Table size='small'>
        <StatsTableHeader groups={groups} leftHeader='Season' leftColSpan={2} />
        <TableBody>
          {seasons.map(season => {
            const row = totalCells(season.statsTotals)
            const seasonLeaves = getVisibleFederationLeaves(season.displayStats)
            return (
              <TableRow
                key={`${season.seasonId}-${season.teamId}`}
                hover
                onClick={onSeasonClick ? () => onSeasonClick(season) : undefined}
                style={onSeasonClick ? { cursor: 'pointer' } : undefined}
              >
                <TableCell className={classes.leftCell}>{season.seasonName}</TableCell>
                <TableCell className={classes.teamCell}>
                  <span className={classes.teamCellInner}>
                    {season.teamThreeLetterIdentifier || season.teamName}
                    {season.teamLogoUrl && (
                      <img
                        src={season.teamLogoUrl}
                        alt={season.teamName}
                        className={classes.teamLogo}
                      />
                    )}
                  </span>
                </TableCell>
                {LEAVES.map(leaf => (
                  <TableCell
                    key={leaf.key}
                    className={classnames(classes.cell, {
                      [classes.categoryBorderLeft]: leaf.firstOfGroup,
                    })}
                  >
                    {seasonLeaves && !seasonLeaves.has(leaf.key) ? '-' : row[leaf.key]}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
          <TableRow className={classes.summaryRow}>
            <TableCell colSpan={2} className={classes.leftCell}>
              <Typography className={classes.summaryLabel}>Total</Typography>
            </TableCell>
            {LEAVES.map(leaf => (
              <TableCell
                key={leaf.key}
                className={classnames(classes.cell, {
                  [classes.categoryBorderLeft]: leaf.firstOfGroup,
                })}
              >
                {totalsRow[leaf.key]}
              </TableCell>
            ))}
          </TableRow>
          <TableRow className={classes.summaryRow}>
            <TableCell colSpan={2} className={classes.leftCell}>
              <Typography className={classes.summaryLabel}>Average</Typography>
            </TableCell>
            {LEAVES.map(leaf => (
              <TableCell
                key={leaf.key}
                className={classnames(classes.cell, {
                  [classes.categoryBorderLeft]: leaf.firstOfGroup,
                })}
              >
                {averagesRow[leaf.key]}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

PlayerCompetitionStatsTable.propTypes = {
  competition: PropTypes.shape({
    competitionName: PropTypes.string,
    seasons: PropTypes.array,
    totals: PropTypes.object,
    averages: PropTypes.object,
  }),
  onSeasonClick: PropTypes.func,
  displayStats: PropTypes.array,
}

export default PlayerCompetitionStatsTable
